"use client"

export const dynamic = 'force-dynamic'

import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Shield, Eye, Lock, Database, UserCheck, Globe } from "lucide-react"

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground">
              Your privacy is our priority. Learn how we protect and handle your data.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: December 2024
            </p>
          </div>

          {/* Privacy Principles */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card>
              <CardHeader className="text-center">
                <Shield className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <CardTitle className="text-lg">Data Protection</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground">
                  We use industry-standard encryption and security measures
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="text-center">
                <Eye className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <CardTitle className="text-lg">Transparency</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground">
                  Clear information about what data we collect and why
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="text-center">
                <UserCheck className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <CardTitle className="text-lg">Your Control</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-sm text-muted-foreground">
                  You control your data with rights to access, modify, and delete
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-8">
            {/* Information We Collect */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-blue-600" />
                  Information We Collect
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Account Information</h3>
                  <p className="text-muted-foreground text-sm">
                    When you create an account, we collect your email address, name, and authentication information. This data is necessary for account creation and service access.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Translation Files</h3>
                  <p className="text-muted-foreground text-sm">
                    We temporarily store your uploaded JSON files to perform translations. Files are encrypted at rest and automatically deleted after 30 days unless you choose to keep them.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">API Keys</h3>
                  <p className="text-muted-foreground text-sm">
                    Your OpenAI API keys are encrypted using AES-256 encryption before storage. We never log or access the content of your API keys beyond using them for translation requests.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Usage Data</h3>
                  <p className="text-muted-foreground text-sm">
                    We collect basic usage statistics like number of translations, file sizes, and API response times to improve our service. This data is anonymized and aggregated.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Information */}
            <Card>
              <CardHeader>
                <CardTitle>How We Use Your Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• Provide and maintain our translation service</li>
                  <li>• Process your translation requests using your OpenAI API key</li>
                  <li>• Send you important service updates and security notifications</li>
                  <li>• Improve our service through anonymized usage analytics</li>
                  <li>• Provide customer support when you contact us</li>
                  <li>• Ensure service security and prevent abuse</li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Data Sharing and Third Parties
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">OpenAI</h3>
                  <p className="text-muted-foreground text-sm">
                    Your translation content is sent to OpenAI for processing using your API key. This is essential for our service function. OpenAI's privacy policy governs their handling of this data.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Service Providers</h3>
                  <p className="text-muted-foreground text-sm">
                    We use trusted third-party services for hosting, analytics, and customer support. These providers are bound by strict data protection agreements.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Legal Requirements</h3>
                  <p className="text-muted-foreground text-sm">
                    We may disclose information if required by law, legal process, or to protect the rights and safety of our users and service.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-green-600" />
                  Data Security
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  We implement comprehensive security measures to protect your data:
                </p>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• AES-256 encryption for sensitive data at rest</li>
                  <li>• TLS 1.2+ encryption for all data in transit</li>
                  <li>• Regular security audits and penetration testing</li>
                  <li>• Access controls and monitoring for our systems</li>
                  <li>• Secure data centers with physical security measures</li>
                  <li>• Regular backup and disaster recovery procedures</li>
                </ul>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card>
              <CardHeader>
                <CardTitle>Your Rights and Choices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">You have the following rights regarding your personal data:</p>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• <strong>Access:</strong> Request a copy of your personal data</li>
                  <li>• <strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li>• <strong>Deletion:</strong> Request deletion of your account and data</li>
                  <li>• <strong>Portability:</strong> Export your data in a standard format</li>
                  <li>• <strong>Objection:</strong> Object to certain data processing activities</li>
                  <li>• <strong>Restriction:</strong> Request limitation of data processing</li>
                </ul>
                <p className="text-muted-foreground text-sm mt-4">
                  To exercise these rights, contact us at privacy@locsy.ai or through your account settings.
                </p>
              </CardContent>
            </Card>

            {/* Cookies and Tracking */}
            <Card>
              <CardHeader>
                <CardTitle>Cookies and Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  We use minimal cookies and tracking technologies:
                </p>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>• <strong>Essential cookies:</strong> Required for authentication and security</li>
                  <li>• <strong>Analytics:</strong> Anonymized usage data to improve our service</li>
                  <li>• <strong>Preferences:</strong> Remember your settings and preferences</li>
                </ul>
                <p className="text-muted-foreground text-sm mt-4">
                  You can manage cookie preferences through your browser settings.
                </p>
              </CardContent>
            </Card>

            {/* Children's Privacy */}
            <Card>
              <CardHeader>
                <CardTitle>Children's Privacy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately.
                </p>
              </CardContent>
            </Card>

            {/* International Data Transfers */}
            <Card>
              <CardHeader>
                <CardTitle>International Data Transfers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers, including standard contractual clauses and adequacy decisions where applicable.
                </p>
              </CardContent>
            </Card>

            {/* Changes to Privacy Policy */}
            <Card>
              <CardHeader>
                <CardTitle>Changes to This Privacy Policy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
                </p>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-2 text-muted-foreground text-sm">
                  <p>Email: privacy@locsy.ai</p>
                  <p>Address: 123 Tech Street, San Francisco, CA 94102, United States</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
} 